import React, { Fragment } from 'react';
import { CheckIcon } from 'lucide-react';
import { motion } from 'framer-motion';
interface Step {
  id: number;
  title: string;
}
interface StepIndicatorProps {
  steps: Step[];
  currentStep: number;
  onStepClick: (index: number) => void;
  mode: 'onboarding' | 'offboarding';
  setMode: (mode: 'onboarding' | 'offboarding') => void;
}
export function StepIndicator({
  steps,
  currentStep,
  onStepClick,
  mode,
  setMode
}: StepIndicatorProps) {
  return <div className="flex flex-col h-full" data-id="element-227" data-mp="e348zwhjq8c6">
      {/* Onboarding / Offboarding Toggle */}
      <div className="hidden md:flex p-1 bg-gray-100 rounded-lg mb-8 mx-4 mt-6 relative" data-id="element-228" data-mp="e4xirkngh8oe">
        <motion.button layoutId="onboarding-toggle" onClick={() => setMode('onboarding')} className={`flex-1 py-1.5 text-xs font-semibold rounded-md transition-all z-10 ${mode === 'onboarding' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-229" data-mp="3vhhsm44yfkwc">
          Onboarding
        </motion.button>
        <motion.button layoutId="offboarding-toggle" onClick={() => setMode('offboarding')} className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all z-10 ${mode === 'offboarding' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-230" data-mp="1relh3a1znvs">
          Offboarding
        </motion.button>
      </div>

      {/* Vertical Steps (Desktop) */}
      <div className="hidden md:flex flex-col space-y-1 px-3" data-id="element-231" data-mp="3f845vvoqkli9">
        {steps.map((step, index) => {
        const isCompleted = index < currentStep;
        const isActive = index === currentStep;
        const isClickable = isCompleted;
        return <button key={step.id} onClick={() => isClickable && onStepClick(index)} disabled={!isClickable && !isActive} className={`flex items-center w-full text-left px-3 py-2.5 rounded-lg transition-all relative ${isActive ? 'bg-blue-50' : isClickable ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default opacity-60'}`} data-id="element-232" data-mp="3k6p3rogimt3j" data-mpi={index}>
              {isActive && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-6 bg-navy-600 rounded-r-full" data-id="element-233" data-mp="3l6fnryhhasjw" />}

              <div className="flex-shrink-0 mr-3" data-id="element-234" data-mp="3pyz9jiqbktzs">
                {isCompleted ? <div className="w-6 h-6 rounded-full bg-[#2E844A] flex items-center justify-center" data-id="element-235" data-mp="3uwsi2if3i038">
                    <CheckIcon className="w-3.5 h-3.5 text-white" data-id="element-236" data-mp="2y8ujb3fvpcqn" />
                  </div> : isActive ? <div className="w-6 h-6 rounded-full bg-navy-600 flex items-center justify-center" data-id="element-237" data-mp="32ag2t7mx4p9t">
                    <span className="text-white text-xs font-bold" data-id="element-238" data-mp="37a2dpaf9xtfg">
                      {step.id}
                    </span>
                  </div> : <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center" data-id="element-239" data-mp="mh8sbtdmuzsx">
                    <span className="text-gray-500 text-xs font-bold" data-id="element-240" data-mp="reqt2whhy8pu">
                      {step.id}
                    </span>
                  </div>}
              </div>
              <span className={`text-sm font-medium ${isActive ? 'text-navy-600' : isCompleted ? 'text-gray-700' : 'text-gray-500'}`} data-id="element-241" data-mp="2701ed1gaq2bi">
                {step.title}
              </span>
            </button>;
      })}
      </div>

      {/* Horizontal Steps (Mobile) */}
      <div className="md:hidden flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-white" data-id="element-242" data-mp="1t9fvvdur7ue5">
        {steps.map((step, index) => {
        const isCompleted = index < currentStep;
        const isActive = index === currentStep;
        const isClickable = isCompleted;
        return <Fragment key={step.id} data-id="element-243" data-mp="1ed8aia7m0mxa" data-mpi={index}>
              <button onClick={() => isClickable && onStepClick(index)} disabled={!isClickable && !isActive} className="flex flex-col items-center group relative" data-id="element-244" data-mp="3do8156zcp4se">
                <div className="flex-shrink-0 mb-1" data-id="element-245" data-mp="3gqvxyjf7nb70">
                  {isCompleted ? <div className="w-6 h-6 rounded-full bg-[#2E844A] flex items-center justify-center" data-id="element-246" data-mp="3lncp7mhpi5ob">
                      <CheckIcon className="w-3.5 h-3.5 text-white" data-id="element-247" data-mp="36qlptd818typ" />
                    </div> : isActive ? <div className="w-6 h-6 rounded-full bg-navy-600 flex items-center justify-center ring-4 ring-navy-50" data-id="element-248" data-mp="3bsko48ox10os">
                      <span className="text-white text-xs font-bold" data-id="element-249" data-mp="32pjn2mewpzrz">
                        {step.id}
                      </span>
                    </div> : <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-250" data-mp="2nz2nz9pqvly9">
                      <span className="text-gray-400 text-xs font-bold" data-id="element-251" data-mp="2f07d3kbr9nom">
                        {step.id}
                      </span>
                    </div>}
                </div>
                <span className={`text-[10px] font-medium absolute -bottom-4 whitespace-nowrap ${isActive ? 'text-navy-600' : 'text-gray-400'}`} data-id="element-252" data-mp="20dijfz46q4q5">
                  {isActive ? step.title : ''}
                </span>
              </button>
              {index < steps.length - 1 && <div className={`flex-1 h-[2px] mx-2 rounded-full ${index < currentStep ? 'bg-[#2E844A]' : 'bg-gray-100'}`} data-id="element-253" data-mp="24elw6cqxrkn5" />}
            </Fragment>;
      })}
      </div>
    </div>;
}